// ============================================================
// หน้า TempRecordWaterUsage — บันทึกค่าใช้จ่าย (แบบพักไว้ก่อน)
// ------------------------------------------------------------
// ต่างจากหน้า RecordWaterUsage ตรงที่บันทึกลง "ตารางพัก" (record-water-temp) ก่อน
// ให้ตรวจทานทั้งชุดได้ แล้วค่อยกด "ส่งข้อมูลค่าใช้จ่าย" เพื่อย้ายเข้าระบบจริงทีเดียว
//
// ลำดับการทำงาน
//   1. โหลดตั้งค่าโครงการ → โหลดรายการที่พักไว้ → โหลดบ้านเลขที่ที่ยังไม่ได้จด
//   2. จดทีละห้อง กด "บันทึกข้อมูล" → เข้าตารางพักด้านล่าง แล้วเด้งไปห้องถัดไปอัตโนมัติ
//   3. แก้ค่าใช้จ่ายรายการใดรายการหนึ่งของ "ทุกห้องพร้อมกัน" ได้ ด้วยปุ่มสีเหลืองเหนือตาราง
//   4. กด "ส่งข้อมูลค่าใช้จ่าย" → ย้ายทุกใบเข้าระบบจริง ลบออกจากตารางพัก
//      แล้วคิดยอดค้าง/เบี้ยปรับใหม่ทั้งโครงการ (calculate_water)
//
// การกรอกใช้แป้นพิมพ์ได้: Enter / ลูกศรขวา = ช่องถัดไป, ลูกศรซ้าย = ช่องก่อนหน้า
// ============================================================

import React, { Component } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";

import "../../shared/config/global.js";
import "../../assets/css/bootstrap.css";
import Navbar from "../../shared/layout/Navbar";
import {
  loading,
  tokens,
  select_style,
  alert,
  success,
  alert_url,
  toFixed,
  GET,
  POST,
  PUT,
  DELETE,
  format_date,
  float,
  DatePicker,
  getStorage,
  calculate_water,
  cal_water_bill,
  getWaterSetting,
  calcFireCost,
  calcCommonExpenses,
  billTotal,
  permission,
  ActionIcon,
  ICON,
} from "../../shared/index.js";

const perm = permission("record-water-usage");

// สถานะมิเตอร์ของห้อง
const METER_STATUS = [
  { value: 0, label: "" },
  { value: 1, label: "ปกติ" },
  { value: 2, label: "ตัดมิเตอร์" },
];
const meterStatusOption = (value) => METER_STATUS.find((s) => s.value === value) || METER_STATUS[0];

/* หาแถวซ้ำในตารางพัก (บ้านเลขที่ + เดือน/ปีของวันที่บันทึก — ตรงกับที่หลังบ้านใช้กันบิลซ้ำ)
 * ไฮไลต์เฉพาะแถวที่ซ้ำเกินมา (แถวแรกของกลุ่มไม่นับ) — ใช้แสดงผลเท่านั้น ไม่กระทบยอด */
const dupPeriod = (i) => (i.datetime_create ? format_date(i.datetime_create, "mm/yyyy") : "");
const dupKeyOf = (i) => (i.address_number || "") + "|" + dupPeriod(i);
function findDupIndexes(data) {
  const seen = new Set();
  const dup = new Set();
  (data || []).forEach((i, idx) => {
    if (!i.address_number || !i.datetime_create) return;
    const k = dupKeyOf(i);
    if (seen.has(k)) dup.add(idx);
    else seen.add(k);
  });
  return dup;
}
const DUP_BG = "#fdecec";
const DUP_TITLE = "แถวซ้ำ: ห้องนี้มีรายการงวดเดียวกันอยู่แล้ว (ซ้ำกับแถวก่อนหน้า)";

// ป้ายบ้านเลขที่ในช่องเลือก
const addressLabel = (item) => `อาคาร ${item.building} ห้อง ${item.address_number}`;

// รายการค่าใช้จ่ายที่แก้ทีเดียวทุกห้องได้ (ปุ่มสีเหลืองเหนือตาราง)
const BULK_COSTS = [
  { id: "equipment_maintenance_fee", name: "ค่ารักษาอุปกรณ์น้ำ" },
  { id: "common_expenses", name: "ค่าส่วนกลาง" },
  { id: "waste_cost", name: "ค่าขยะ" },
  { id: "cable_cost", name: "ค่าเคเบิล" },
  { id: "fire_cost", name: "ค่าเบี้ยประกัน" },
  { id: "other_income", name: "รายได้อื่นๆ" },
  { id: "add_debt", name: "เพิ่มหนี้" },
  { id: "reduce_debt", name: "ลดหนี้" },
];

// ฟิลด์ตัวเลขที่ต้องแปลงเป็น Number ก่อนส่ง backend
const NUMERIC_FIELDS = ["last_gauge_number", "current_gauge_number", "unit_used", "water_bill", "equipment_maintenance_fee", "common_expenses", "waste_cost", "cable_cost", "other_income", "add_debt", "reduce_debt", "fire_cost"];

// หัวคอลัมน์ตารางรายการที่พักไว้
const TABLE_HEADERS = [
  "บ้านเลขที่",
  "วันที่บันทึก",
  "เลขมาตรเดือนนี้",
  "เลขมาตรก่อน",
  "จำนวนหน่วยที่ใช้",
  "ค่าน้ำประปา",
  "ค่ารักษาอุปกรณ์น้ำ",
  "ค่าส่วนกลาง",
  "ค่าขยะ",
  "ค่าเคเบิล",
  "ค่าเบี้ยประกัน",
  "รายได้อื่นๆ",
  "เพิ่มหนี้",
  "ลดหนี้",
  "รวมเป็นเงิน",
  "จัดการ",
];

export default class RecordWaterUsage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // popup สรุปแถวซ้ำ (เลือกแล้วลบออกจากตารางพัก)
      modal_dup: false,
      dup_rows: [],
      dup_selected: [],
      dup_note: "",

      update: false, // true = โหมดแก้ไขรายการพักที่มีอยู่
      data: { data: [] }, // รายการที่พักไว้ (ตารางด้านล่าง)
      data_address: null, // บ้านเลขที่ที่ยังไม่ได้จดในรอบนี้

      // popup แก้ค่าใช้จ่ายทุกห้องพร้อมกัน
      modal_cost: false,
      status_cost: "", // ชื่อฟิลด์ที่จะแก้
      cost: "", // ค่าที่จะใส่แทน

      // ข้อมูลที่กำลังกรอก
      record_water_id: "",
      address_number: null,
      building: "",
      full_name: "",
      room_size: 0,
      status_meter: 0,
      date_of_note: new Date(),
      last_gauge_number: 0,
      current_gauge_number: 0,
      unit_used: 0,

      // ค่าใช้จ่ายในบิล
      water_bill: 0,
      equipment_maintenance_fee: 0,
      common_expenses: 0,
      waste_cost: 0,
      cable_cost: 0,
      status_cable: false,
      fire_cost: 0,
      other_income: 0,
      add_debt: 0,
      reduce_debt: 0,
      remark_add_debt: "",
      remark_reduce_debt: "",
      total_amount: 0,

      // ค่าจากตั้งค่าโครงการ
      setting_water_id: getStorage("water_id"),
      water_charge_start: 0,
      water_charge_end: 0,
      water_charge_price: 0,
      water_rate_list: [],
      water_calculate_type: 2,
      common_expenses_type: false,
      common_expenses_text: 0,
      payment_deadline: 0,
      penalty_day_list: [],
      penalty_month_list: [],
      fire_month: "",
      max_date: null,
    };
  }

  async componentDidMount() {
    if (!perm.view) return;
    loading(true);
    await this.GetSetting();
    await this.GetAll();
    await this.GetAddressNumberAll();
    loading(false);
  }

  // ---------- ตัวช่วย ----------

  pid = () => getStorage("project_id");

  editingId = () => this.props.location.query.id;

  // ยอดรวมของบิล — ค่าเคเบิลนับเฉพาะรายการที่เปิดเก็บ (status_cable)
  getTotal = (s = this.state) => billTotal({ ...s, cable_cost: s.status_cable ? s.cable_cost : 0 });

  // คิดค่าน้ำใหม่จากหน่วยที่ใช้ปัจจุบัน
  cal_water = () => this.setState({ water_bill: cal_water_bill(this.state.unit_used, this.state) });

  // หน่วยที่ใช้ = เลขมาตรเดือนนี้ - เลขมาตรเดือนก่อน แล้วคิดค่าน้ำใหม่
  recalcUnit = () => this.setState({ unit_used: Number(this.state.current_gauge_number) - Number(this.state.last_gauge_number) }, this.cal_water);

  // ---------- โหลดข้อมูล ----------

  GetSetting = async () => {
    const d = await getWaterSetting(this.state.setting_water_id);
    if (!d) return;

    // หา "รอบเดือน" ที่ควรจด — ยังไม่ถึงวันครบกำหนดชำระ แปลว่ายังอยู่ในรอบของเดือนก่อน
    let date_of_note = new Date();
    if (d.payment_deadline) {
      const deadline = new Date();
      deadline.setDate(Number(d.payment_deadline));
      if (new Date() < deadline) deadline.setMonth(deadline.getMonth() - 1);
      date_of_note = deadline;
    }

    await new Promise((resolve) => {
      this.setState(
        {
          water_charge_start: Number(d.water_charge.water_charge_start),
          water_charge_end: Number(d.water_charge.water_charge_end),
          water_charge_price: toFixed(Number(d.water_charge.water_charge_price)),
          water_rate_list: d.water_rate_list,
          water_calculate_type: Number(d.water_calculate_type) || 2,
          fire_cost: calcFireCost(d, format_date(new Date(), "m")),
          fire_month: d.fire_month,
          common_expenses_type: d.common_expenses_type,
          common_expenses_text: d.common_expenses,
          payment_deadline: d.payment_deadline,
          penalty_day_list: d.penalty_day_list,
          penalty_month_list: d.penalty_month_list,
          date_of_note,
        },
        resolve
      );
    });

    // ตั้งค่าไม่ครบ = คิดเงินไม่ได้ ต้องไล่ไปตั้งค่าก่อน
    if (!d.payment_close_time || !d.meter_cut_date || !d.penalty_day_list.length || !d.penalty_month_list.length) {
      alert_url("warning", "แจ้งเตือน", "ท่านยังไม่ทำการตั้งค่าอัตราค่าน้ำก่อนทำการเริ่มต้นใช้งานระบบ", "/manage-water");
    }
  };

  // รายการที่พักไว้ (เรียงกลับด้านให้ใบล่าสุดอยู่บนสุด)
  GetAll = async () => {
    const r = await GET(tokens, `v1/TH/record-water-temp/filter-search/page/0/${this.pid()}`, { address_number: this.state.filter_address_number?.value || "" });
    if (r?.status) this.setState({ data: { ...r.data, data: r.data.data.reverse() } });
  };

  // บ้านเลขที่ที่ยังต้องจด — ตัดห้องที่พักไว้แล้วออก
  GetAddressNumberAll = async () => {
    const r = await GET(tokens, `v1/TH/record-water/address-list/${this.pid()}`, {
      now_month: Number(format_date(this.state.date_of_note, "mm")),
      now_year: Number(format_date(this.state.date_of_note, "yyyy", "en")),
    });
    if (!r?.status) return;

    const list = r.data.data
      .filter((i) => !this.state.data?.data?.some((e) => e.address_number === i.address_number))
      .sort((a, b) => a.address_number.localeCompare(b.address_number, undefined, { numeric: true }));
    this.setState({ data_address: list });

    if (this.editingId()) {
      this.setState({ update: true, record_water_id: this.editingId() }, () => this.GetById(this.editingId()));
    } else if (!list.length) {
      alert("warning", "แจ้งเตือน", "ไม่มีข้อมูลบ้านเลขที่ในเดือนนี้");
    } else {
      // เปิดบ้านหลังแรกที่เหลือให้เลย แล้วเด้ง cursor ไปช่องเลขมาตรเดือนนี้
      this.setState({ address_number: { value: list[0].address_number, label: addressLabel(list[0]) }, building: list[0].building }, () => {
        this.GetAddressById();
        setTimeout(() => document.getElementById("current_gauge_number")?.focus(), 100);
      });
    }
  };

  // ข้อมูลตั้งต้นของบ้านที่เลือก
  GetAddressById = async () => {
    const r = await GET(tokens, `v1/TH/record-water/address-number/detail/${this.pid()}`, {
      address_number: this.state.address_number?.value || "",
      date_of_note: format_date(this.state.date_of_note || new Date(), "yyyy-mm-dd", "en"),
    });
    if (!r?.status) return;

    const d = r.data.data;
    this.setState({
      full_name: d.full_name,
      room_size: d.room_size,
      last_gauge_number: d.last_gauge_number,
      // ตั้งเลขมาตรเดือนนี้เท่าเดือนก่อนไว้ก่อน (หน่วยที่ใช้ = 0) รอผู้ใช้กรอกทับ
      current_gauge_number: d.last_gauge_number,
      unit_used: 0,
      water_bill: Number(this.state.water_charge_price), // ยังไม่ใช้น้ำ = จ่ายอัตราขั้นต่ำ
      cable_cost: d.cable_cost,
      common_expenses: calcCommonExpenses(this.state.common_expenses_type, d.common_expenses, d.room_size),
      equipment_maintenance_fee: d.equipment_maintenance_fee,
      waste_cost: d.waste_cost,
      status_meter: meterStatusOption(d.status_meter),
      status_cable: d.status_cable || Number(d.cable_cost) !== 0,
      // ล้างค่าที่กรอกเองของห้องก่อนหน้า
      other_income: 0,
      add_debt: 0,
      reduce_debt: 0,
      fire_cost: 0,
      remark_add_debt: "",
      remark_reduce_debt: "",
    });
  };

  // โหลดรายการพักที่จะแก้ไข
  GetById = async (id) => {
    loading(true);
    const r = await GET(tokens, `v1/TH/record-water-temp/detail/${id}`, null);
    if (!r?.status) return;

    const d = r.data.data;
    this.setState(
      {
        data_address: [...(this.state.data_address || []), { value: d.address_number, label: addressLabel(d) }],
        record_water_id: d.record_water_id,
        address_number: { value: d.address_number, label: addressLabel(d) },
        building: d.building,
        last_gauge_number: d.last_gauge_number,
        current_gauge_number: d.current_gauge_number,
        unit_used: d.unit_used,
        water_bill: d.water_bill,
        equipment_maintenance_fee: d.equipment_maintenance_fee,
        common_expenses: d.common_expenses,
        waste_cost: d.waste_cost,
        cable_cost: d.cable_cost,
        status_cable: d.status_cable || Number(d.cable_cost) !== 0,
        other_income: d.other_income,
        add_debt: d.add_debt,
        reduce_debt: d.reduce_debt,
        fire_cost: d.fire_cost,
        remark_add_debt: d.remark_add_debt,
        remark_reduce_debt: d.remark_reduce_debt,
        total_amount: d.total_amount,
        room_size: d.room_size,
        status_meter: meterStatusOption(d.status_meter),
        date_of_note: d.datetime_create,
      },
      () => {
        // แก้ไขได้ถึงวันครบกำหนดชำระของรอบนั้น
        if (this.editingId()) {
          const max = new Date(d.datetime_create);
          max.setDate(this.state.payment_deadline);
          this.setState({ max_date: format_date(max, "yyyy-mm-dd", "en") });
        }
        this.cal_water();
        loading(false);
      }
    );
  };

  // ---------- บันทึก / ลบ ----------

  processSave = async (isUpdate) => {
    if (Number(this.state.unit_used) < 0) return alert("warning", "แจ้งเตือน", "ไม่สามารถกรอกเลขมาตรในเดือนนี้น้อยกว่าเดือนก่อนได้");
    loading(true);

    const datetime = isUpdate ? this.state.date_of_note : format_date(this.state.date_of_note, "yyyy-mm-dd hh:mm", "en");
    const data = {
      ...this.state,
      address_number: this.state.address_number?.value || "",
      total_amount: this.getTotal(),
      cable_cost: this.state.status_cable ? Number(this.state.cable_cost) : 0,
      datetime_create: datetime,
      status_meter: this.state.status_meter?.value || this.state.status_meter || 0,
    };
    if (!isUpdate) data.date_of_note = datetime;

    const r = isUpdate ? await PUT(tokens, "v1/TH/record-water-temp/update", data) : await POST(tokens, `v1/TH/record-water-temp/create/${this.pid()}`, data);
    if (!r?.status) {
      loading(false);
      return;
    }

    if (isUpdate) {
      alert_url("success", "สำเร็จ", "บันทึกข้อมูลเรียบร้อยแล้ว", "/temp-record-water-usage");
      loading(false);
      return;
    }

    // จดใหม่สำเร็จ → ตัดห้องที่เพิ่งจดออกจากรายการ แล้วเปิดห้องถัดไปให้ทันที
    const remaining = this.state.data_address.filter((i) => i.address_number !== this.state.address_number?.value);
    this.setState(
      {
        data_address: remaining,
        address_number: remaining[0] ? { value: remaining[0].address_number, label: addressLabel(remaining[0]) } : null,
        building: remaining[0]?.building || "",
      },
      async () => {
        if (remaining[0]) this.GetAddressById();
        loading(false);
        await success();
        document.getElementById("current_gauge_number")?.focus();
        this.GetAll();
      }
    );
  };

  Create = () => this.processSave(false);
  Update = () => this.processSave(true);

  // แก้ค่าใช้จ่าย 1 รายการของ "ทุกห้อง" ในตารางพักพร้อมกัน แล้วคิดยอดรวมใหม่ทุกใบ
  UpdateCost = async () => {
    loading(true);
    const body = {
      data: this.state.data.data.map((item) => {
        const updated = { ...item };
        NUMERIC_FIELDS.forEach((key) => (updated[key] = Number(item[key] || 0)));
        if (this.state.status_cost) updated[this.state.status_cost] = Number(this.state.cost);
        updated.total_amount = this.getTotal(updated);
        return updated;
      }),
    };
    const r = await PUT(tokens, "v1/TH/record-water-temp/update-all", body);
    if (r?.status) alert_url("success", "สำเร็จ", "บันทึกข้อมูลเรียบร้อยแล้ว", "/temp-record-water-usage");
    loading(false);
    this.setState({ modal_cost: false });
  };

  Delete = async (id) => {
    loading(true);
    const r = await DELETE(tokens, `v1/TH/record-water-temp/delete/${id}`, null);
    if (r?.status) {
      success();
      this.clearBillForm();
      await this.GetAddressNumberAll();
      this.GetAddressById();
      this.GetAll();
    }
    loading(false);
  };

  clearBillForm = () => {
    this.setState({
      last_gauge_number: 0,
      current_gauge_number: 0,
      unit_used: 0,
      water_bill: 0,
      address_number: null,
      building: "",
      equipment_maintenance_fee: 0,
      common_expenses: 0,
      waste_cost: 0,
      cable_cost: 0,
      other_income: 0,
      add_debt: 0,
      reduce_debt: 0,
      remark_add_debt: "",
      remark_reduce_debt: "",
      total_amount: 0,
      full_name: "",
      room_size: 0,
      status_meter: 0,
    });
  };

  // ยืนยันก่อนลบรายการพัก
  confirmDelete = (item) => {
    Swal.fire({
      icon: "warning",
      title: "ยืนยันการลบข้อมูล",
      text: "คุณต้องการลบข้อมูลนี้ใช่หรือไม่",
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "ลบข้อมูล",
      cancelButtonText: "ยกเลิก",
    }).then((res) => {
      if (res.isConfirmed) this.Delete(item.record_water_id);
    });
  };

  // ย้ายทุกรายการในตารางพักเข้าระบบจริง
  // สร้างบิลจริงทีละใบ ใบไหนสำเร็จถึงจะลบออกจากตารางพัก ใบที่ล้มเหลว (เช่น บิลงวดเดียวกันซ้ำ) ไม่ทำให้ใบอื่นหยุด
  // แล้วรวบใบที่ล้มเหลวไปแสดงใน popup สรุปแถวซ้ำ
  SendData = () => {
    Swal.fire({
      title: "ยืนยันการบันทึกข้อมูล",
      text: "คุณต้องการบันทึกข้อมูลนี้ใช่หรือไม่?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "บันทึกข้อมูล",
      cancelButtonText: "ยกเลิก",
    }).then(async (res) => {
      if (!res.isConfirmed) return;
      loading(true);
      try {
        const rows = this.state.data.data;
        const results = await Promise.allSettled(
          rows.map(async (item) => {
            const body = {
              ...item,
              status_cable: !!Number(item.cable_cost),
              date_of_note: format_date(item.datetime_create, "yyyy-mm-dd hh:mm", "en"),
              datetime_create: format_date(item.datetime_create, "yyyy-mm-dd hh:mm", "en"),
            };
            body.total_amount = this.getTotal(body);
            const r = await POST(tokens, `v1/TH/record-water/create/${this.pid()}`, body);
            if (!r?.status) throw new Error("POST_FAILED");
            // สร้างบิลสำเร็จแล้ว: ถ้าลบแถวพักไม่ได้ ต้องแยกจากกรณีส่งไม่สำเร็จ (ห้ามส่งซ้ำ)
            const d = await DELETE(tokens, `v1/TH/record-water-temp/delete/${item.record_water_id}`, null);
            if (!d?.status) throw new Error("DELETE_FAILED");
          })
        );
        const failedAll = rows.map((row, idx) => ({ row, res: results[idx] })).filter((x) => x.res.status === "rejected");
        const failed = failedAll.map((x) => ({ ...x.row, _reason: x.res.reason?.message === "DELETE_FAILED" ? "สร้างบิลสำเร็จแล้ว แต่ลบแถวพักไม่สำเร็จ (ห้ามส่งซ้ำ ให้ลบแถวนี้ออกจากตารางพัก)" : "ส่งไม่สำเร็จ โปรดตรวจสอบก่อนลบ" }));
        const billedCount = rows.length - failedAll.filter((x) => x.res.reason?.message !== "DELETE_FAILED").length;
        const okCount = rows.length - failed.length;
        const unsentCount = failed.filter((x) => x._reason.startsWith("ส่งไม่สำเร็จ")).length;
        // ออกบิลจริงแล้วอย่างน้อย 1 ใบ ต้องคิดยอดค้าง/เบี้ยปรับใหม่ทั้งโครงการ
        if (billedCount) await calculate_water();
        if (!failed.length) {
          alert_url("success", "สำเร็จ", "บันทึกข้อมูลเรียบร้อยแล้ว", "/temp-record-water-usage");
        } else {
          await this.GetAll();
          // helper POST เด้ง Swal แจ้ง error ของแต่ละแถว (ซ้อนกันเหลืออันสุดท้าย) — เก็บข้อความล่าสุดไว้ แล้วปิดก่อนเปิด modal สรุปรวมครั้งเดียว
          let sysMsg = "";
          if (Swal.isVisible()) { sysMsg = (Swal.getHtmlContainer()?.textContent || "").trim(); Swal.close(); }
          const parts = [`ส่งสำเร็จ ${okCount} รายการ`];
          if (unsentCount) parts.push(`ส่งไม่สำเร็จ ${unsentCount} รายการ`);
          if (failed.length - unsentCount) parts.push(`สร้างบิลแล้วแต่ลบแถวพักไม่สำเร็จ ${failed.length - unsentCount} รายการ`);
          this.openDupModal(failed, parts.join(" · ") + (sysMsg ? ` · ข้อความล่าสุดจากระบบ: ${sysMsg}` : "") + ' · ดูสถานะรายแถวในตาราง เลือกแถวที่ไม่ต้องการแล้วกด "ลบแถวที่เลือก" ได้ (ลบเฉพาะรายการที่พักไว้ ไม่กระทบบิลจริง)');
        }
      } catch (e) {
        console.error(e);
      }
      loading(false);
    });
  };

  // ---------- จัดการแถวซ้ำ ----------

  // เปิดหน้าต่างสรุปแถวซ้ำ (rows = แถวที่จะให้เลือกลบ, note = ข้อความอธิบายหัวตาราง)
  openDupModal = (rows, note) => this.setState({ modal_dup: true, dup_rows: rows, dup_selected: [], dup_note: note });

  openDupFromTable = () => {
    const list = this.state.data?.data || [];
    const dupSet = findDupIndexes(list);
    this.openDupModal(list.filter((_, idx) => dupSet.has(idx)), "แถวที่ซ้ำเกินมาในตารางพัก (บ้านเลขที่และเดือน/ปีเดียวกัน)");
  };

  toggleDup = (id) => this.setState((st) => ({ dup_selected: st.dup_selected.includes(id) ? st.dup_selected.filter((x) => x !== id) : [...st.dup_selected, id] }));

  toggleDupAll = () => this.setState((st) => ({ dup_selected: st.dup_selected.length === st.dup_rows.length ? [] : st.dup_rows.map((i) => i.record_water_id) }));

  // ลบเฉพาะแถวที่เลือก "ในตารางพัก" เท่านั้น (record-water-temp) ไม่แตะบิลจริง
  DeleteDupSelected = () => {
    const ids = this.state.dup_selected;
    if (!ids.length) return;
    Swal.fire({
      icon: "warning",
      title: "ยืนยันการลบแถวที่เลือก",
      text: `ต้องการลบ ${ids.length} รายการที่เลือกออกจากตารางพักใช่หรือไม่ (ลบเฉพาะรายการที่พักไว้ ไม่กระทบบิลจริง)`,
      showCancelButton: true,
      cancelButtonColor: "#d33",
      confirmButtonText: "ลบแถวที่เลือก",
      cancelButtonText: "ยกเลิก",
    }).then(async (res) => {
      if (!res.isConfirmed) return;
      loading(true);
      const results = await Promise.allSettled(ids.map((id) => DELETE(tokens, `v1/TH/record-water-temp/delete/${id}`, null)));
      const okIds = ids.filter((_, i) => results[i].status === "fulfilled" && results[i].value?.status);
      const left = this.state.dup_rows.filter((i) => !okIds.includes(i.record_water_id));
      this.setState({ dup_rows: left, dup_selected: [], modal_dup: left.length > 0 });
      await this.GetAll();
      loading(false);
      if (okIds.length) success();
    });
  };

  // ---------- ตัวช่วยแสดงผล ----------

  // จัดการปุ่มลูกศร/Enter ระหว่างช่องกรอก
  //   Enter / ลูกศรขวา = ไปช่องถัดไป ("SUBMIT" = กดบันทึก)
  //   ลูกศรซ้าย        = กลับช่องก่อนหน้า
  // เช็ค .swal2-container ก่อน เพื่อไม่ให้กด Enter ปิด popup แล้วบันทึกซ้ำ
  onKey = (nextId, prevId) => (e) => {
    if (e.key === "Enter" || e.key === "ArrowRight") {
      if (document.querySelector(".swal2-container") || !nextId) return;
      if (nextId === "SUBMIT") (this.state.update ? this.Update : this.Create)();
      else document.getElementById(nextId)?.focus();
    } else if (e.key === "ArrowLeft" && prevId) {
      document.getElementById(prevId)?.focus();
    }
  };

  // ช่องกรอกในฟอร์ม
  //   isNumber = true รับเฉพาะตัวเลข (ว่าง = 0)
  //   opts: { id, disabled, extra (element ข้างป้ายชื่อ), onAfterChange, onKeyUp }
  renderField = (label, field, isNumber = true, opts = {}) => (
    <div className="col-12 col-md-6 col-lg-3 mb-2">
      <label>
        {label} {opts.extra}
      </label>
      <input
        id={opts.id || field}
        className="form-control bg-contain border-0 mx-1 mb-2"
        placeholder="กรอกข้อมูล"
        value={this.state[field]}
        disabled={opts.disabled}
        onChange={(e) => {
          const value = e.target.value;
          if (isNumber && !float(value)) return;
          this.setState({ [field]: isNumber && value === "" ? 0 : value }, opts.onAfterChange);
        }}
        onKeyUp={opts.onKeyUp}
      />
    </div>
  );

  render() {
    const s = this.state;
    const dupSet = findDupIndexes(s.data?.data);

    return (
      <div className="w-100 min-vh-100 fade-in">
        <Navbar page1="หน้าหลัก" page2="บันทึกค่าใช้จ่าย" />
        <div className="body">
          <div className="p-3">
            <div className="d-flex justify-content-end">
              {/* NOTE ซ่อนปุ่ม "ตรวจสูตรคำนวณค่าน้ำ" — เดิมเปิด {REACT_APP_API_URL}/test-water
                  แต่ backend New CDS P1 ไม่มี route นี้ (มีเฉพาะ CDS_Backend เดิม) กดแล้วได้ 404 */}
              <button className="btn btn-outline-primary px-4 mx-1 mb-3" onClick={() => (window.location.href = "/water-usage-info")}>
                รายการทั้งหมด
              </button>
            </div>

            {/* ฟอร์มจดค่าใช้จ่ายทีละห้อง */}
            <div className="w-100">
              <div className="card border-0 p-3 mb-3">
                <h4 className="mb-3">
                  <b>
                    บันทึกค่าใช้จ่าย เดือน{format_date(new Date(), "mmmm")} พ.ศ. {format_date(new Date(), "yyyy")}
                  </b>
                </h4>
                <div className="row">
                  <div className="col-12 col-md-6 col-lg-3 mb-2">
                    <label>บ้านเลขที่</label>
                    <Select
                      className="form-control bg-contain border-0 w-100 select-search"
                      styles={select_style}
                      options={s.data_address?.map((i) => ({ ...i, value: i.address_number, label: addressLabel(i) })) || []}
                      onChange={(e) => this.setState({ address_number: e, building: e ? e.building : "" }, this.GetAddressById)}
                      value={s.address_number}
                    />
                  </div>

                  {this.renderField("ชื่อ-นามสกุล", "full_name", false, { disabled: true })}
                  {this.renderField("ขนาดห้องที่อยู่อาศัย (ตรม.)", "room_size", false, { disabled: true, onAfterChange: () => this.setState({ room_size: toFixed(s.room_size) }) })}

                  <div className="col-12 col-md-6 col-lg-3 mb-2">
                    <label>สถานะมิเตอร์น้ำ</label>
                    <Select className="form-control bg-contain border-0 w-100 select-search" styles={select_style} options={METER_STATUS} value={s.status_meter} isDisabled={true} />
                  </div>

                  <div className="col-12 col-md-6 col-lg-3 mb-2">
                    <label>วันที่จด</label>
                    <DatePicker
                      className="form-control bg-contain border-0 w-100"
                      value={s.date_of_note}
                      min={s.max_date ? format_date(new Date(new Date(s.max_date).setDate(new Date(s.max_date).getDate() - 1)), "yyyy-mm-dd", "en") : null}
                      max={s.max_date || null}
                      onChange={(e) => this.setState({ date_of_note: e }, () => !this.editingId() && this.GetAddressNumberAll())}
                    />
                  </div>

                  {this.renderField("เลขมาตรเดือนก่อน", "last_gauge_number", true, { onAfterChange: this.recalcUnit, onKeyUp: this.onKey("current_gauge_number") })}
                  {this.renderField("เลขมาตรเดือนนี้", "current_gauge_number", true, { onAfterChange: this.recalcUnit, onKeyUp: this.onKey("SUBMIT", "last_gauge_number") })}
                  {this.renderField("จำนวนหน่วยที่ใช้", "unit_used", false, { disabled: true })}

                  {this.renderField("ค่าน้ำประปา", "water_bill")}
                  {this.renderField("ค่ารักษาอุปกรณ์", "equipment_maintenance_fee")}
                  {this.renderField(`ค่าใช้จ่ายส่วนกลาง ${s.common_expenses_type ? "( " + toFixed(s.common_expenses_text) + " บาท / ตรม. )" : ""}`, "common_expenses")}
                  {this.renderField("ค่าขยะ", "waste_cost")}
                  {/* ค่าเคเบิลมี checkbox กำกับ — ปิดแล้วค่าจะถูกล้างเป็น 0 */}
                  {this.renderField("ค่าเคเบิล", "cable_cost", true, {
                    extra: s.cable_cost !== 0 && <input className="ml-2" type="checkbox" onChange={(e) => this.setState({ status_cable: e.target.checked })} checked={s.status_cable} />,
                    onAfterChange: () => this.state.cable_cost !== 0 && !this.state.status_cable && this.setState({ cable_cost: 0 }),
                  })}
                  {this.renderField("ค่าเบี้ยประกัน", "fire_cost")}
                  {this.renderField("รายได้อื่น", "other_income")}
                  {this.renderField("เพิ่มหนี้", "add_debt")}
                  {this.renderField("หมายเหตุเพิ่มหนี้", "remark_add_debt", false)}
                  {this.renderField("ลดหนี้", "reduce_debt")}
                  {this.renderField("หมายเหตุลดหนี้", "remark_reduce_debt", false)}
                </div>

                <div className="d-flex align-items-end justify-content-between">
                  <div>
                    <label>รวมเป็นเงิน</label>
                    <h4>
                      <b>฿{toFixed(this.getTotal())}</b>
                    </h4>
                  </div>
                  {perm.create && (
                    <button className="btn btn-primary px-4" onClick={() => (s.update ? this.Update() : this.Create())}>
                      บันทึกข้อมูล
                    </button>
                  )}
                </div>

                <small className="text-secondary">
                  หมายเหตุ
                  <br />- Enter หรือ ลูกศรขวา เพื่อไปยังช่องกรอกข้อมูลถัดไป
                  <br />- ลูกศรซ้าย เพื่อกลับไปยังช่องกรอกข้อมูลก่อนหน้า
                </small>
              </div>
            </div>
          </div>

          {/* ตารางรายการที่พักไว้ */}
          <div className="p-3 pt-0">
            <div className="card border-0 p-3">
              <h4 className="mb-3">
                <b>รายการบันทึกค่าใช้จ่าย</b>
              </h4>
              {dupSet.size > 0 && (
                <div className="d-flex align-items-center mb-2">
                  <span className="small" style={{ color: "#b42318" }}>พบแถวซ้ำเกินมา {dupSet.size} รายการ (ไฮไลต์สีแดงอ่อนในตาราง)</span>
                  <button className="btn btn-sm btn-outline-danger ml-3 ms-3" onClick={this.openDupFromTable}>จัดการแถวซ้ำ</button>
                </div>
              )}
              <div className="table-responsive">
                <table className="table table-borderless table-striped">
                  <thead>
                    {/* แถบปุ่มแก้ค่าใช้จ่ายทีเดียวทุกห้อง */}
                    <tr>
                      <th className="p-0" colSpan={6} rowSpan={2}></th>
                      <th className="text-center bg-light fw-bold" colSpan={8}>
                        แก้ไขค่าใช้จ่ายทั้งหมด
                      </th>
                      <th className="text-center" colSpan={2}></th>
                    </tr>
                    <tr>
                      {BULK_COSTS.map((c) => (
                        <th key={c.id} className="text-center bg-light pt-0 pb-2">
                          <button className="rounded-3 px-3 py-2 border-0 bg-warning text-dark text-10 text-nowrap" onClick={() => this.setState({ modal_cost: true, cost: "", status_cost: c.id })}>
                            แก้ไข{c.name}
                          </button>
                        </th>
                      ))}
                      <th className="text-center" colSpan={2}></th>
                    </tr>
                    <tr className="bg-dark-blue text-white">
                      {TABLE_HEADERS.map((h, i) => (
                        <th key={i} className={i === 1 ? "text-left text-nowrap" : "text-center text-nowrap"}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!s.data?.data?.length ? (
                      <tr>
                        <td colSpan={16} className="text-center">
                          ไม่มีข้อมูล
                        </td>
                      </tr>
                    ) : (
                      s.data.data.map((item, index) => {
                        const dupStyle = dupSet.has(index) ? { backgroundColor: DUP_BG, boxShadow: "none" } : undefined;
                        return (
                        <tr key={index} style={dupStyle} title={dupSet.has(index) ? DUP_TITLE : undefined}>
                          <td className="text-center" style={dupStyle}>{item.address_number || "-"}</td>
                          <td className="text-left" style={dupStyle}>{item.datetime_create ? format_date(item.datetime_create) : "0"}</td>
                          {["current_gauge_number", "last_gauge_number", "unit_used"].map((k) => (
                            <td key={k} className="text-right" style={dupStyle}>
                              {item[k] || "0"}
                            </td>
                          ))}
                          {["water_bill", "equipment_maintenance_fee", "common_expenses", "waste_cost", "cable_cost", "fire_cost", "other_income", "add_debt", "reduce_debt", "total_amount"].map((k) => (
                            <td key={k} className="text-right" style={dupStyle}>
                              {toFixed(item[k])}
                            </td>
                          ))}
                          <td className="text-center" style={dupStyle}>
                            <div className="d-flex justify-content-center">
                              <ActionIcon color="primary" icon={ICON.edit} title="แก้ไข" className="mr-2" onClick={() => (window.location.href = "/temp-record-water-usage?id=" + item.record_water_id)} />
                              <ActionIcon color="danger" icon={ICON.paper} title="ลบ" className="mr-2" onClick={() => this.confirmDelete(item)} />
                            </div>
                          </td>
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="d-flex justify-content-end">{perm.create && <button className="btn btn-primary px-5" onClick={this.SendData}>ส่งข้อมูลค่าใช้จ่าย</button>}</div>
            </div>
          </div>
        </div>

        {/* popup แก้ค่าใช้จ่ายทุกห้องพร้อมกัน */}
        <Modal show={s.modal_cost} onHide={() => this.setState({ modal_cost: false })}>
          <Modal.Header>
            <Modal.Title>
              <b>แก้ไขค่าใช้จ่าย</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="w-100 d-flex align-items-center mb-3">
              <label className="wpx-140">{BULK_COSTS.find((c) => c.id === s.status_cost)?.name || ""} :</label>
              <input className="form-control border-0 bg-contain" type="text" placeholder="กรอกข้อมูล" onChange={(e) => float(e.target.value) && this.setState({ cost: e.target.value })} value={s.cost} />
            </div>
            <div className="w-100 d-flex">
              <div className="w-50 p-1">
                <button className="btn btn-outline-primary w-100" onClick={() => this.setState({ modal_cost: false })}>
                  ยกเลิก
                </button>
              </div>
              <div className="w-50 p-1">
                <button className="btn btn-primary w-100" onClick={this.UpdateCost}>
                  บันทึก
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* หน้าต่างสรุปแถวซ้ำ — เลือกแถวแล้วลบออกจากตารางพัก */}
        <Modal show={s.modal_dup} onHide={() => this.setState({ modal_dup: false })} size="lg">
          <Modal.Header>
            <Modal.Title>
              <b>รายการซ้ำ</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3">{s.dup_note}</p>
            <div className="table-responsive mb-3">
              <table className="table table-borderless table-striped mb-0">
                <thead>
                  <tr className="bg-dark-blue text-white">
                    <th className="text-center">
                      <input type="checkbox" title="เลือกทั้งหมด" checked={s.dup_rows.length > 0 && s.dup_selected.length === s.dup_rows.length} onChange={this.toggleDupAll} />
                    </th>
                    <th className="text-center">บ้านเลขที่</th>
                    <th className="text-left">วันที่บันทึก</th>
                    <th className="text-center">งวด</th>
                    <th className="text-right">ยอดรวม</th>
<th className="text-left">สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {s.dup_rows.map((i) => (
                    <tr key={i.record_water_id}>
                      <td className="text-center">
                        <input type="checkbox" checked={s.dup_selected.includes(i.record_water_id)} onChange={() => this.toggleDup(i.record_water_id)} />
                      </td>
                      <td className="text-center">{i.address_number || "-"}</td>
                      <td className="text-left">{i.datetime_create ? format_date(i.datetime_create) : "-"}</td>
                      <td className="text-center">{dupPeriod(i) || "-"}</td>
                      <td className="text-right">{toFixed(i.total_amount)}</td>
                      <td className="text-left small">{i._reason || "แถวซ้ำในตารางพัก"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="w-100 d-flex">
              <div className="w-50 p-1">
                <button className="btn btn-outline-primary w-100" onClick={() => this.setState({ modal_dup: false })}>
                  ปิด
                </button>
              </div>
              <div className="w-50 p-1">
                <button className="btn btn-danger w-100" disabled={!s.dup_selected.length} onClick={this.DeleteDupSelected}>
                  ลบแถวที่เลือก{s.dup_selected.length ? ` (${s.dup_selected.length})` : ""}
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}
